import { forwardRef } from 'react'
import * as Label from '@radix-ui/react-label'
import { AlertCircle } from 'lucide-react'
import clsx from 'clsx'

import { LIST_NAME_MAX_LENGTH } from '../../../utils/favouriteLists'

interface ListNameFieldProps {
  id: string
  value: string
  onChange: (value: string) => void
  error: string | null
}

/**
 * Name input shared by the sheet's "New list" row and the New list / Rename
 * dialogs. The hint and the error share one slot, linked by
 * aria-describedby, so the error replaces the hint rather than stacking.
 * The input stops at 40 characters, so the hint is the only length message
 * (product decision, replacing AC5's "Use 40 characters or fewer" error).
 */
export const ListNameField = forwardRef<HTMLInputElement, ListNameFieldProps>(
  ({ id, value, onChange, error }, ref) => {
    const messageId = `${id}-message`
    return (
      <div>
        <Label.Root htmlFor={id} className="block text-sm font-medium text-gray-900">
          List name
        </Label.Root>
        <input
          ref={ref}
          id={id}
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          maxLength={LIST_NAME_MAX_LENGTH}
          aria-invalid={error ? true : undefined}
          aria-describedby={messageId}
          autoComplete="off"
          enterKeyHint="done"
          className={clsx(
            'mt-2 h-12 w-full rounded-xl border bg-white px-4 text-base text-gray-900 placeholder:text-gray-400',
            'focus:outline-none focus:ring-2',
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary',
          )}
        />
        {error ? (
          <p id={messageId} className="mt-1.5 flex items-start gap-1.5 text-sm font-medium text-red-600">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span className="min-w-0 break-words">{error}</span>
          </p>
        ) : (
          <p id={messageId} className="mt-1.5 text-xs text-gray-500">
            For example, Tue EV job. Up to {LIST_NAME_MAX_LENGTH} characters.
          </p>
        )}
      </div>
    )
  },
)

ListNameField.displayName = 'ListNameField'
